import React, { useContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  LeftOutlined,
  RightOutlined,
  UpOutlined,
  DownOutlined,
} from '@ant-design/icons';

import {
  Button,
  Card,
  Col,
  Empty,
  Popconfirm,
  Row,
  Select,
  Space,
  Table,
  Typography,
} from 'antd';
import { MinusCircleOutlined } from '@ant-design/icons';

import {
  ANALYSIS_TYPES,
  BUY,
  cancelOrder,
  cancelOrders,
  CREATED,
  CREATING,
  fetchBlotter,
  FROZEN,
  OpenOrdersErrorType,
  PAUSED,
  selectors,
  TERMS,
  TRANSACTION_TYPES,
  UnderlyingAsset,
} from 'state';
import { formatters } from 'services';
import type { TableColumnsType as ColumnsType } from 'antd';
import { auctions_tenors } from '../../../state/app/auctions/types';
import { underlyingAssetsList } from '../../../state/app/selectors';
import { TraineeSessionContext } from '../TraineeSessionContext';
import OptionsChain from './OptionsChain';
import SmileChart from './SmileChart';
import TermStructure from './TermStructure';
import CurrentSpotPosition from './CurrentSpotPosition';
import OptionsTradeView from './OptionsTradeView';
import { TraineeOptionsContext } from '../TraineeOptionsContext';
import OrderSummary from './OptionsOrderSummary';
import { LoadingScreen } from 'components';

const OptionsMarkets: React.FC = () => {
  const dispatch = useDispatch();

  const { scenario_date } = useSelector(selectors.activeSession);
  const underlyingAssetsList = useSelector(selectors.underlyingAssetsList);
  const optionAssets: UnderlyingAsset[] = underlyingAssetsList.filter(
    item => item.resourcetype === 'Option'
  );
  const optionsContext = useContext(TraineeOptionsContext);

  const expirationDates = useSelector(
    selectors.getExpirationDates(
      optionsContext.selectedOptionAsset || optionAssets[0].id
    )
  );

  if (optionsContext.selectedOptionAsset === undefined) {
    optionsContext.setSelectedOptionAsset(optionAssets[0].id);
  }
  if (
    optionsContext.selectedExpirationDate === undefined ||
    !expirationDates.includes(optionsContext.selectedExpirationDate)
  ) {
    optionsContext.setSelectedExpirationDate(expirationDates[0]);
  }

  // Handlers to move between expiration terms
  const handleLowerTerm = () => {
    const current = optionsContext.selectedExpirationDate;
    const idx = expirationDates.findIndex(d => d === current);
    if (idx > 0) {
      optionsContext.setSelectedExpirationDate(expirationDates[idx - 1]);
    }
  };

  const handleHigherTerm = () => {
    const current = optionsContext.selectedExpirationDate;
    const idx = expirationDates.findIndex(d => d === current);
    if (idx < expirationDates.length - 1) {
      optionsContext.setSelectedExpirationDate(expirationDates[idx + 1]);
    }
  };

  return (
    <div>
      <Row gutter={80} style={{ flexFlow: 'row nowrap' }}>
        <Col span={5} style={{ minWidth: '390px' }}>
          <Card title={'Select Option'} size={'small'}>
            {/*<Space align="center">*/}
            <Select
              // allowClear
              showSearch
              style={{ width: '190px' }}
              optionFilterProp="children"
              value={optionsContext.selectedOptionAsset}
              onChange={value => optionsContext.setSelectedOptionAsset(value)}
            >
              {optionAssets.map(item => {
                return (
                  <Select.Option key={item.id} value={item.id}>
                    {item.name}
                  </Select.Option>
                );
              })}
            </Select>
            <Space>
              <Select
                style={{ width: '190px' }}
                value={optionsContext.selectedExpirationDate}
                onChange={value =>
                  optionsContext.setSelectedExpirationDate(value)
                }
              >
                {expirationDates.map(expirationDateString => {
                  const scenarioDate = new Date(scenario_date);
                  const expirationDate = new Date(expirationDateString);
                  const diffMs =
                    expirationDate.getTime() - scenarioDate.getTime();
                  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

                  return (
                    <Select.Option
                      key={expirationDateString}
                      value={expirationDateString}
                    >
                      {diffDays} days
                    </Select.Option>
                  );
                })}
              </Select>
              <Button
                type={'primary'}
                onClick={handleHigherTerm}
                icon={<UpOutlined />}
              />
              <Button
                type={'primary'}
                onClick={handleLowerTerm}
                icon={<DownOutlined />}
              />
            </Space>
          </Card>
          <Card
            title={'ATM IV % Chg'}
            size={'small'}
            style={{ marginTop: '20px' }}
          >
            <Typography.Text strong>68.5%</Typography.Text>
            <Typography.Text type={'secondary'}>
              {' '}
              (since last period)
            </Typography.Text>
          </Card>
          <OptionsTradeView
            asset_id={optionsContext.selectedOptionAsset || optionAssets[0].id}
            expiration={
              optionsContext.selectedExpirationDate || expirationDates[0]
            }
          />
        </Col>
        <Col
          span={19}
          style={{
            width: 'min-content',
            display: 'flex',
            flexDirection: 'column',
            gap: 40,
            borderLeft: '1px solid #eeeeee',
            // height: '70vh',
          }}
        >
          <Row style={{ flexFlow: 'row nowrap', minWidth: '1200px' }}>
            {' '}
            <OptionsChain
              asset_id={
                optionsContext.selectedOptionAsset || optionAssets[0].id
              }
              expiration={
                optionsContext.selectedExpirationDate || expirationDates[0]
              }
            />
          </Row>
          <Row
            gutter={30}
            style={{ flexFlow: 'row nowrap', minWidth: '1200px' }}
          >
            <Col span={7}>
              <SmileChart
                asset_id={
                  optionsContext.selectedOptionAsset || optionAssets[0].id
                }
                expiration={
                  optionsContext.selectedExpirationDate || expirationDates[0]
                }
              />
            </Col>
            <Col span={7}>
              <TermStructure
                asset_id={
                  optionsContext.selectedOptionAsset || optionAssets[0].id
                }
                expiration={
                  optionsContext.selectedExpirationDate || expirationDates[0]
                }
              />
            </Col>
            <Col span={10}>
              <CurrentSpotPosition
                asset_id={
                  optionsContext.selectedOptionAsset || optionAssets[0].id
                }
                expiration={
                  optionsContext.selectedExpirationDate || expirationDates[0]
                }
              />
            </Col>
          </Row>
        </Col>
      </Row>
      <div style={{ width: '850px' }}>
        <OrderSummary
          asset_id={optionsContext.selectedOptionAsset || optionAssets[0].id}
          expiration={
            optionsContext.selectedExpirationDate || expirationDates[0]
          }
        />
      </div>
    </div>
  );
};

export default OptionsMarkets;
